import React from 'react';
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Button,
  AlertTitle,
} from '@mui/material';
import { motion } from 'framer-motion';
import { Refresh, Clear } from '@mui/icons-material';
import DoctorCard from './DoctorCard';

const DoctorsList = ({
  doctors = [],
  count = 0,
  loading = false,
  error = null,
  viewMode = 'grid',
  filters = {},
  onRefresh,
  onClearFilters,
  onFavoriteToggle,
  onSelect,
  onBookAppointment,
  onViewProfile,
  onShare,
  favorites = [],
}) => {
  // Validate doctors data
  const validDoctors = Array.isArray(doctors) ? doctors : [];
  const hasDoctors = validDoctors.length > 0;
  const isLoading = loading && !hasDoctors;
  const isRefreshing = loading && hasDoctors;

  // Loading skeleton
  if (isLoading) {
    return (
      <Box sx={{ py: 4 }}>
        <Grid container spacing={3}>
          {[...Array(6)].map((_, index) => (
            <Grid item xs={12} md={viewMode === 'grid' ? 6 : 12} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ width: 60, height: 60, borderRadius: '50%', bgcolor: 'grey.300', mr: 2 }} />
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ width: '60%', height: 24, bgcolor: 'grey.300', mb: 1 }} />
                      <Box sx={{ width: '40%', height: 16, bgcolor: 'grey.300' }} />
                    </Box>
                  </Box>
                  <Box sx={{ width: '80%', height: 16, bgcolor: 'grey.300', mb: 1 }} />
                  <Box sx={{ width: '60%', height: 16, bgcolor: 'grey.300', mb: 2 }} />
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <Box sx={{ width: 80, height: 24, bgcolor: 'grey.300', borderRadius: 1 }} />
                    <Box sx={{ width: 80, height: 24, bgcolor: 'grey.300', borderRadius: 1 }} />
                  </Box>
                  <Box sx={{ width: '100%', height: 40, bgcolor: 'grey.300', borderRadius: 1 }} />
                </Box>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  // Enhanced error state with better user feedback
  if (error) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>Error Loading Doctors</AlertTitle>
          {error}
        </Alert>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button 
            variant="contained" 
            onClick={onRefresh}
            startIcon={<Refresh />}
          >
            Try Again
          </Button>
          <Button 
            variant="outlined" 
            onClick={onClearFilters}
            startIcon={<Clear />}
          >
            Clear Filters
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          If the problem persists, please check your internet connection or try refreshing the page.
        </Typography>
      </Box>
    );
  }

  // No doctors found - Enhanced empty state
  if (!hasDoctors && !loading) {
    const hasActiveFilters = filters?.query || filters?.location || filters?.speciality || 
                            filters?.language || filters?.min_rating || filters?.min_fee || 
                            filters?.max_fee || filters?.min_experience || filters?.gender ||
                            filters?.availability || filters?.consultation_type;
    
    return (
      <Box sx={{ 
        textAlign: 'center', 
        py: { xs: 6, md: 10 },
        px: 2
      }}>
        <Box sx={{ 
          maxWidth: 500, 
          mx: 'auto',
          mb: 4
        }}>
          <Typography 
            variant="h5" 
            sx={{ 
              mb: 2,
              fontWeight: 600,
              color: 'text.primary'
            }}
          >
            {hasActiveFilters ? 'No doctors match your criteria' : 'No doctors found'}
          </Typography>
          <Typography 
            variant="body1" 
            color="text.secondary" 
            sx={{ 
              mb: 4,
              lineHeight: 1.7
            }}
          >
            {hasActiveFilters 
              ? `We couldn't find any doctors matching your search filters. Try adjusting your criteria or clearing some filters to see more results.`
              : 'No doctors are currently available. Please check back later or try refreshing the page.'
            }
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            {hasActiveFilters && (
              <Button 
                variant="contained" 
                onClick={onClearFilters}
                startIcon={<Clear />}
                size="large"
                sx={{ 
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                Clear All Filters
              </Button>
            )}
            <Button 
              variant={hasActiveFilters ? "outlined" : "contained"}
              onClick={onRefresh}
              startIcon={<Refresh />}
              size="large"
              sx={{ 
                px: 3,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Refresh
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  // Show refreshing overlay - show existing data with loading indicator
  if (isRefreshing) {
    return (
      <Box sx={{ position: 'relative' }}>
        <Box sx={{ 
          position: 'sticky',
          top: 16,
          zIndex: 10,
          mb: 2,
          p: 1.5,
          borderRadius: 2,
          backgroundColor: 'primary.light',
          border: '1px solid',
          borderColor: 'primary.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: 2
        }}>
          <CircularProgress size={20} sx={{ mr: 1.5, color: 'primary.main' }} />
          <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
            Updating results...
          </Typography>
        </Box>
        
        {/* Doctors list with reduced opacity during refresh */}
        <Box sx={{ opacity: 0.7, transition: 'opacity 0.3s' }}>
          <Grid container spacing={{ xs: 2, sm: 2, md: 3 }}>
            {validDoctors.map((doctor, idx) => (
              <Grid 
                item 
                xs={12} 
                sm={viewMode === 'grid' ? 6 : 12} 
                md={viewMode === 'grid' ? 6 : 12} 
                key={doctor.id || idx}
              >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ 
                  duration: 0.4, 
                  delay: Math.min(idx * 0.05, 0.5),
                  type: "spring",
                  stiffness: 100
                }}
                whileHover={{ 
                  y: -4,
                  transition: { duration: 0.2 }
                }}
                style={{ height: '100%' }}
              >
                  <DoctorCard 
                    doctor={doctor}
                    viewMode={viewMode}
                    isFavorite={favorites.includes(doctor.id)}
                    onFavoriteToggle={() => onFavoriteToggle(doctor.id)}
                    onSelect={() => onSelect(doctor)}
                    onBookAppointment={() => onBookAppointment(doctor)}
                    onViewProfile={() => onViewProfile(doctor)}
                    onShare={() => onShare(doctor)}
                  />
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
    );
  }

  // Normal doctors list
  return (
    <Grid container spacing={{ xs: 2, sm: 2, md: 3 }}>
      {validDoctors.map((doctor, idx) => (
        <Grid 
          item 
          xs={12} 
          sm={viewMode === 'grid' ? 6 : 12} 
          md={viewMode === 'grid' ? 6 : 12} 
          key={doctor.id || idx}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ 
              duration: 0.4, 
              delay: Math.min(idx * 0.05, 0.5), // Limit max delay for better UX
              type: "spring",
              stiffness: 100
            }}
            whileHover={{ 
              y: -4,
              transition: { duration: 0.2 }
            }}
            style={{ height: '100%' }}
          >
            <DoctorCard 
              doctor={doctor}
              viewMode={viewMode}
              isFavorite={favorites.includes(doctor.id)}
              onFavoriteToggle={() => onFavoriteToggle(doctor.id)}
              onSelect={() => onSelect(doctor)}
              onBookAppointment={() => onBookAppointment(doctor)}
              onViewProfile={() => onViewProfile(doctor)}
              onShare={() => onShare(doctor)}
            />
          </motion.div>
        </Grid>
      ))}
    </Grid>
  );
};

export default DoctorsList; 